import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'

import { NoticeLoading } from '@fs/zion-icon'
import { BrightspotProvider } from '@fs/zion-cms'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import useAlertBanner from './hooks/useAlertBanner'
import AlertBannerContext from './hooks/AlertBannerContext'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// Add any app logic here as flags, languages and translations are now configured and available
const FrontierWrapper = () => {
  const alertBannerProps = useAlertBanner()
  return (
    <BrightspotProvider sites={['locations']} subsites={['locations']}>
      <Router basename={base}>
        <Root analytics alertBannerProps={alertBannerProps}>
          <AlertBannerContext.Provider value={alertBannerProps}>
            <AppLayout fullWidth>
              <App />
            </AppLayout>
          </AlertBannerContext.Provider>
        </Root>
      </Router>
    </BrightspotProvider>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </React.StrictMode>
  )
}

ReactDOM.render(
  <Suspense fallback={<NoticeLoading />}>
    <FrontierRoot />
  </Suspense>,
  document.getElementById('root')
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
